'use client'

import { useEffect } from 'react'

interface SkipLinkProps {
  href: string
  children: React.ReactNode
  className?: string
  title?: string
}

export default function SkipLink({ href, children, title, className = 'skip-link' }: SkipLinkProps) {
  useEffect(() => {
    const skipLink = document.querySelector(`.${className}`) as HTMLAnchorElement

    if (!skipLink) return

    // Remove focus if it somehow gets auto-focused on page load
    if (document.activeElement === skipLink) {
      skipLink.blur()
    }

    // Prevent mouse interactions to keep it keyboard-only
    const handleMouseDown = (e: MouseEvent) => {
      e.preventDefault()
    }

    // Handle click to actually skip to content when activated via keyboard
    const handleClick = (e: MouseEvent | KeyboardEvent) => {
      // Only allow if focused (meaning it was accessed via keyboard)
      if (document.activeElement === skipLink) {
        const target = document.querySelector(href) as HTMLElement
        if (target) {
          e.preventDefault()
          target.focus()
          target.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      } else {
        e.preventDefault()
      }
    }

    // Add event listeners
    skipLink.addEventListener('mousedown', handleMouseDown)
    skipLink.addEventListener('click', handleClick)

    // Cleanup function
    return () => {
      skipLink.removeEventListener('mousedown', handleMouseDown)
      skipLink.removeEventListener('click', handleClick)
    }
  }, [href, className])

  return (
    <a href={href} className={className} tabIndex={0} title={title} aria-label={title}>
      {children}
    </a>
  )
}
